import { getAppDisplayName } from './publicAppSlugs.js';

const hoursFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0
});
const moneyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 0,
  style: 'currency'
});
const compactMoneyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 1,
  notation: 'compact',
  style: 'currency'
});

const toFiniteNumber = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return 0;
  }

  return numberValue;
};

const getUniqueAppSlugs = (appSlugs = []) => {
  return appSlugs.filter((slug, index) => {
    return Boolean(slug) && appSlugs.indexOf(slug) === index;
  });
};

const formatMoney = (value) => {
  const moneySaved = toFiniteNumber(value);

  if (Math.abs(moneySaved) >= 1000000) {
    return compactMoneyFormatter.format(moneySaved);
  }

  return moneyFormatter.format(moneySaved);
};

const formatAppList = (appNames = []) => {
  if (appNames.length <= 1) {
    return appNames[0] || 'MindCloud';
  }

  return `${appNames.slice(0, -1).join(', ')} and ${appNames.at(-1)}`;
};

export const buildAppIntegrationRoiProof = ({ appSlugs = [], roiBreakdown } = {}) => {
  const uniqueAppSlugs = getUniqueAppSlugs(appSlugs);

  if (uniqueAppSlugs.length === 0 || !Array.isArray(roiBreakdown?.apps)) {
    return null;
  }

  const appsBySlug = new Map(
    roiBreakdown.apps
      .filter((app) => {
        return typeof app?.slug === 'string' && app.slug !== '';
      })
      .map((app) => {
        return [app.slug, app];
      })
  );
  const apps = uniqueAppSlugs.map((slug) => {
    return appsBySlug.get(slug);
  });

  if (apps.some((app) => !app)) {
    return null;
  }

  const totals = apps.reduce(
    (accumulator, app) => {
      return {
        hoursSaved: accumulator.hoursSaved + toFiniteNumber(app.hoursSaved),
        moneySaved: accumulator.moneySaved + toFiniteNumber(app.moneySaved)
      };
    },
    {
      hoursSaved: 0,
      moneySaved: 0
    }
  );

  if (totals.hoursSaved <= 0 && totals.moneySaved <= 0) {
    return null;
  }

  return {
    appNames: apps.map((app) => {
      return getAppDisplayName(app);
    }),
    hoursSaved: totals.hoursSaved,
    moneySaved: totals.moneySaved,
    windowLabel: roiBreakdown.windowLabel
  };
};

export const formatAppIntegrationRoiProof = (roiProof) => {
  if (!roiProof) {
    return 'Businesses automate real work through MindCloud every day.';
  }

  return `Businesses save ${hoursFormatter.format(roiProof.hoursSaved)} work hours and ${formatMoney(roiProof.moneySaved)} every month by automating ${formatAppList(roiProof.appNames)}.`;
};

export const buildAppIntegrationRoiProofCopyParts = (roiProof) => {
  if (!roiProof) {
    return {
      fallbackText: formatAppIntegrationRoiProof(roiProof)
    };
  }

  return {
    prefix: 'Businesses save ',
    hoursText: hoursFormatter.format(roiProof.hoursSaved),
    metricText: `${hoursFormatter.format(roiProof.hoursSaved)} work hours and ${formatMoney(roiProof.moneySaved)} every month`,
    moneyText: formatMoney(roiProof.moneySaved),
    suffix: ` by automating ${formatAppList(roiProof.appNames)}.`
  };
};
